import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  Loader2,
  Sparkles,
  Trash2,
  Upload,
} from "lucide-react";
import * as XLSX from "xlsx";
import closeIcon from "../../assets/cross.svg";
import { studentService } from "../../services/studentService";

/**
 * Bulk-import a student roster from a file.
 *
 * Flow:
 *   1. Drag-drop or file-picker upload (CSV / PDF / JPG / PNG, ≤7MB).
 *   2. POST to /v1/admin/roster/extract — gpt-4o reads the file and
 *      returns structured rows + warnings.
 *   3. Editable review table — admin fixes typos, removes garbage rows,
 *      sets a cohort graduation year that applies to every invite.
 *   4. POST to /v1/invites/schools/students with the cleaned names + emails +
 *      expectedGraduationYear. Each student gets an invite email addressed to
 *      them by first name.
 *
 * Intentionally one wide modal (not a multi-step wizard) so admins can
 * scroll back up to swap files without losing review state. Below ~25
 * rows it all fits on one screen; beyond that the rows scroll and the
 * sticky footer keeps the submit CTA in reach.
 */
// Extensions the AI extractor handles directly (sent as-is), plus the
// canonical MIME we send for each. We key off the extension, not File.type:
// browsers report it inconsistently (Windows often calls .csv
// "application/vnd.ms-excel"), which would otherwise bounce a valid file.
const DIRECT_EXT_MIME = {
  ".csv": "text/csv",
  ".txt": "text/plain",
  ".pdf": "application/pdf",
  ".jpg": "image/jpeg",
  ".jpeg": "image/jpeg",
  ".png": "image/png",
};
// Excel files can't be read by the AI as raw bytes (zipped XML), so we parse
// them to CSV in the browser and send that instead.
const SPREADSHEET_EXTS = [".xlsx", ".xls"];
// MIME fallback for when a file has no usable extension but a valid type.
const SUPPORTED_TYPES = Object.values(DIRECT_EXT_MIME);
const MAX_FILE_BYTES = 7 * 1024 * 1024; // matches BE ROSTER_FILE_MAX_BYTES

const fileExt = (name) => {
  const lower = (name || "").toLowerCase();
  const dot = lower.lastIndexOf(".");
  return dot >= 0 ? lower.slice(dot) : "";
};

export default function ImportRosterModal({ onClose, onImportComplete }) {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);

  const [phase, setPhase] = useState("upload"); // upload | extracting | review | inviting
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState({ students: [], warnings: [] });
  const [rows, setRows] = useState([]); // editable copy: { id, firstName, lastName, email, graduationYear, selected }
  const [cohortYear, setCohortYear] = useState(new Date().getFullYear());
  const [error, setError] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  const validRows = useMemo(
    () => rows.filter((r) => r.selected && isValidEmail(r.email)),
    [rows],
  );
  const invalidSelectedCount = rows.filter((r) => r.selected && !isValidEmail(r.email)).length;

  // ─── File pickup ────────────────────────────────────────────────────

  const handleFileChosen = async (chosen) => {
    setError(null);
    if (!chosen) return;
    const ext = fileExt(chosen.name);
    const isSpreadsheet = SPREADSHEET_EXTS.includes(ext);
    const directMime = DIRECT_EXT_MIME[ext] || (SUPPORTED_TYPES.includes(chosen.type) ? chosen.type : null);
    if (!isSpreadsheet && !directMime) {
      setError(
        t("roster.importModal.unsupportedType", {
          defaultValue: "Unsupported file type. Use Excel, CSV, PDF, JPG, or PNG.",
        }),
      );
      return;
    }
    if (chosen.size > MAX_FILE_BYTES) {
      setError(
        t("roster.importModal.tooLarge", {
          defaultValue: "File too large ({{size}}MB). Max 7MB.",
          size: (chosen.size / 1024 / 1024).toFixed(1),
        }),
      );
      return;
    }
    setFile(chosen);
    setPhase("extracting");
    try {
      // Excel → parse the first sheet to CSV in-browser and send that, since
      // the AI extractor can't read raw .xlsx bytes. Everything else goes as-is
      // with an extension-derived MIME (don't trust File.type).
      let base64Data;
      let mimeType;
      if (isSpreadsheet) {
        const buf = await chosen.arrayBuffer();
        const wb = XLSX.read(buf, { type: "array" });
        const sheet = wb.Sheets[wb.SheetNames[0]];
        const csv = sheet ? XLSX.utils.sheet_to_csv(sheet) : "";
        if (!csv.trim()) {
          throw new Error(
            t("roster.importModal.emptySheet", { defaultValue: "That spreadsheet's first sheet looks empty." }),
          );
        }
        // Round-trip through a Blob so UTF-8 names (accents, etc.) base64-encode
        // correctly — btoa would choke on non-Latin1 characters.
        base64Data = await fileToBase64(new Blob([csv], { type: "text/csv" }));
        mimeType = "text/csv";
      } else {
        base64Data = await fileToBase64(chosen);
        mimeType = directMime;
      }
      const result = await studentService.extractRoster({
        base64Data,
        mimeType,
        filename: chosen.name,
      });
      const students = result.students || [];
      setExtracted({ students, warnings: result.warnings || [] });
      // Initialize the editable rows. Pre-tick everything that has a
      // valid email — the most common case is "AI got it right" so
      // default-on selection makes the most-likely path a one-click
      // submit. Rows with missing/invalid emails come in unticked so
      // they surface visually as "needs attention".
      setRows(
        students.map((s, i) => ({
          id: `r${i}`,
          firstName: s.firstName || "",
          lastName: s.lastName || "",
          email: s.email || "",
          graduationYear: s.graduationYear || null,
          selected: isValidEmail(s.email || ""),
        })),
      );
      // Best-guess cohort year: the most common graduation year among
      // the extracted rows. Lets the admin override if needed.
      const yearGuess = mostCommonYear(students);
      if (yearGuess) setCohortYear(yearGuess);
      setPhase("review");
    } catch (err) {
      setError(err.message || "Couldn't extract roster");
      setPhase("upload");
      setFile(null);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) handleFileChosen(dropped);
  };

  // ─── Row mutators ───────────────────────────────────────────────────

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const toggleAll = (selected) => {
    setRows((prev) => prev.map((r) => ({ ...r, selected: selected && isValidEmail(r.email) })));
  };

  // ─── Submit ─────────────────────────────────────────────────────────

  // sendEmails=true → import + email now; false → import as pending only
  // (send later from the Pending Students screen).
  const handleSubmit = async (sendEmails = true) => {
    if (validRows.length === 0) return;
    setError(null);
    setPhase("inviting");
    try {
      // Send the names the extraction already gave us — they drive the
      // "Hi <first name>" greeting in the invite email and show up on the
      // Pending Students list. Previously only emails were sent and the
      // reviewed names were silently thrown away.
      const students = validRows.map((r) => ({
        email: r.email.trim().toLowerCase(),
        firstName: (r.firstName || "").trim() || undefined,
        lastName: (r.lastName || "").trim() || undefined,
      }));
      const result = await studentService.inviteStudents(students, {
        expectedGraduationYear: cohortYear ? Number(cohortYear) : undefined,
        sendEmails,
      });
      // Report new-vs-duplicate so the admin knows how many actually landed.
      // Falls back to the row count if an older API omits the fields.
      const created = result?.created ?? students.length;
      const duplicates = result?.duplicates ?? 0;
      if (created === 0 && duplicates > 0) {
        toast.info(
          t("roster.importModal.toastAllDuplicates", {
            defaultValue: "No new students added — all {{count}} were already in your school.",
            count: duplicates,
          }),
        );
      } else {
        const base = sendEmails
          ? t("roster.importModal.toastSuccess", { defaultValue: "Sent {{count}} invite(s).", count: created })
          : t("roster.importModal.toastImportedOnly", {
              defaultValue: "Imported {{count}} student(s) — send invites anytime from Pending Students.",
              count: created,
            });
        const suffix =
          duplicates > 0
            ? ` ${t("roster.importModal.toastDuplicatesSuffix", { defaultValue: "{{count}} already in your school (skipped).", count: duplicates })}`
            : "";
        toast.success(base + suffix);
      }
      onImportComplete?.(result);
      onClose();
    } catch (err) {
      setError(err.message || "Failed to import roster");
      setPhase("review");
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex justify-center items-center p-4">
      <div className="w-[820px] max-w-full max-h-[92vh] overflow-y-auto bg-white rounded-[20px] font-['Manrope']">
        {/* Header */}
        <div className="p-6 bg-gradient-to-br from-white to-blue-100 border-b border-blue-100 flex justify-between items-start sticky top-0 z-10">
          <div>
            <h2 className="text-[18px] font-bold text-gray-900 inline-flex items-center gap-2">
              <Sparkles size={16} className="text-primary" />
              {t("roster.importModal.title", { defaultValue: "Import roster" })}
            </h2>
            <p className="text-sm text-gray-600 mt-0.5 max-w-[600px]">
              {t("roster.importModal.subtitle", {
                defaultValue:
                  "Upload an Excel, CSV, PDF, or photo of your class list. AI extracts names + emails; you review before any invites are sent.",
              })}
            </p>
          </div>
          <button onClick={onClose} className="p-1" type="button" disabled={phase === "extracting" || phase === "inviting"}>
            <img src={closeIcon} alt="Close" className="w-5 h-5" />
          </button>
        </div>

        {/* Body — branches on phase */}
        <div className="p-6 bg-white flex flex-col gap-5">
          {phase === "upload" && (
            <UploadDropZone
              dragActive={dragActive}
              setDragActive={setDragActive}
              onDrop={handleDrop}
              onPick={() => fileInputRef.current?.click()}
            />
          )}

          {phase === "extracting" && <ExtractingState filename={file?.name} />}

          {phase === "review" && (
            <ReviewState
              rows={rows}
              warnings={extracted.warnings}
              cohortYear={cohortYear}
              setCohortYear={setCohortYear}
              updateRow={updateRow}
              removeRow={removeRow}
              toggleAll={toggleAll}
              filename={file?.name}
              onReupload={() => {
                setPhase("upload");
                setFile(null);
                setExtracted({ students: [], warnings: [] });
                setRows([]);
              }}
              validCount={validRows.length}
              invalidSelectedCount={invalidSelectedCount}
            />
          )}

          {phase === "inviting" && <InvitingState count={validRows.length} />}

          {error && (
            <div className="rounded-[10px] bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Hidden native input for file picker — handles click-to-pick.
              Drag-drop path uses the visible drop zone. */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls,.pdf,.jpg,.jpeg,.png,text/csv,text/plain,application/pdf,image/jpeg,image/png,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
            className="hidden"
            onChange={(e) => handleFileChosen(e.target.files?.[0])}
          />
        </div>

        {/* Footer */}
        <div className="px-6 pb-6 flex justify-end gap-3 bg-white sticky bottom-0 border-t border-gray-100 pt-4">
          <button
            type="button"
            onClick={onClose}
            disabled={phase === "extracting" || phase === "inviting"}
            className="px-5 py-2.5 rounded-[10px] border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {t("roster.importModal.cancel", { defaultValue: "Cancel" })}
          </button>
          {phase === "review" && (
            <>
              {/* Import only: add to the roster as pending, email later. */}
              <button
                type="button"
                onClick={() => handleSubmit(false)}
                disabled={validRows.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("roster.importModal.importOnly", { defaultValue: "Import only" })}
              </button>
              {/* Import + email invites now. */}
              <button
                type="button"
                onClick={() => handleSubmit(true)}
                disabled={validRows.length === 0}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-[10px] bg-primary text-white text-sm font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Upload size={14} />
                {t("roster.importModal.importAndSend", {
                  defaultValue: "Import & send {{count}} invite(s)",
                  count: validRows.length,
                })}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Phase subcomponents ──────────────────────────────────────────────

function UploadDropZone({ dragActive, setDragActive, onDrop, onPick }) {
  const { t } = useTranslation();
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={onDrop}
      onClick={onPick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onPick();
      }}
      className={`rounded-[14px] border-2 border-dashed p-10 flex flex-col items-center text-center cursor-pointer transition ${
        dragActive
          ? "border-primary bg-blue-50/50"
          : "border-gray-300 bg-gray-50 hover:border-primary hover:bg-blue-50/30"
      }`}
    >
      <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-blue-50 text-primary mb-4">
        <FileUp size={22} />
      </div>
      <div className="text-base font-bold text-gray-900">
        {t("roster.importModal.dropTitle", { defaultValue: "Drop your roster here" })}
      </div>
      <div className="text-sm text-gray-500 mt-1">
        {t("roster.importModal.dropSubtitle", {
          defaultValue: "Or click to choose a file. Excel, CSV, PDF, JPG, PNG · up to 7MB",
        })}
      </div>
      <div className="mt-5 inline-flex items-center gap-1.5 text-xs text-gray-400">
        <Sparkles size={12} />
        {t("roster.importModal.dropTip", {
          defaultValue: "AI will extract names + emails. You review before any invites go out.",
        })}
      </div>
    </div>
  );
}

function ExtractingState({ filename }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[14px] border border-gray-200 bg-gray-50 p-10 flex flex-col items-center text-center">
      <Loader2 size={28} className="animate-spin text-primary mb-3" />
      <div className="text-base font-bold text-gray-900">
        {t("roster.importModal.extracting", { defaultValue: "Extracting roster…" })}
      </div>
      <div className="text-sm text-gray-500 mt-1">{filename}</div>
      <div className="text-xs text-gray-400 mt-3 max-w-md">
        {t("roster.importModal.extractingHint", {
          defaultValue: "This usually takes 5–15 seconds. Larger or scanned files take longer.",
        })}
      </div>
    </div>
  );
}

function InvitingState({ count }) {
  const { t } = useTranslation();
  return (
    <div className="rounded-[14px] border border-gray-200 bg-gray-50 p-10 flex flex-col items-center text-center">
      <Loader2 size={28} className="animate-spin text-primary mb-3" />
      <div className="text-base font-bold text-gray-900">
        {t("roster.importModal.inviting", {
          defaultValue: "Sending {{count}} invite(s)…",
          count,
        })}
      </div>
    </div>
  );
}

function ReviewState({
  rows,
  warnings,
  cohortYear,
  setCohortYear,
  updateRow,
  removeRow,
  toggleAll,
  filename,
  onReupload,
  validCount,
  invalidSelectedCount,
}) {
  const { t } = useTranslation();
  const allChecked = rows.length > 0 && rows.every((r) => r.selected);
  return (
    <div className="flex flex-col gap-4">
      {/* File chip + reupload */}
      <div className="flex items-center justify-between gap-2">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-gray-100 text-xs text-gray-700">
          <FileUp size={12} /> {filename}
        </div>
        <button
          type="button"
          onClick={onReupload}
          className="text-xs font-semibold text-primary hover:underline"
        >
          {t("roster.importModal.reupload", { defaultValue: "Use a different file" })}
        </button>
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="rounded-[10px] bg-amber-50 border border-amber-200 px-3 py-2 text-sm">
          <div className="inline-flex items-center gap-1.5 font-semibold text-amber-900 mb-1">
            <AlertTriangle size={12} />
            {t("roster.importModal.warnings", { defaultValue: "Extractor notes" })}
          </div>
          <ul className="list-disc pl-5 text-amber-800 space-y-0.5">
            {warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Cohort year — applies to ALL invites */}
      <div className="flex items-center justify-between rounded-[10px] border border-gray-200 bg-gray-50 px-3 py-2.5">
        <div>
          <div className="text-sm font-semibold text-gray-900">
            {t("roster.importModal.cohortYear", { defaultValue: "Class of" })}
          </div>
          <div className="text-xs text-gray-500">
            {t("roster.importModal.cohortYearHint", {
              defaultValue: "Applies to every invite. Each student will be tagged with this graduation year.",
            })}
          </div>
        </div>
        <input
          type="number"
          value={cohortYear}
          onChange={(e) => setCohortYear(e.target.value)}
          min={1900}
          max={2200}
          className="w-28 px-3 py-2 rounded-[8px] border border-gray-300 text-sm focus:outline-none focus:border-primary"
        />
      </div>

      {/* Counter + select-all */}
      <div className="flex items-center justify-between text-xs">
        <div className="flex gap-4">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <CheckCircle2 size={12} />
            {t("roster.importModal.validCount", { defaultValue: "{{count}} ready", count: validCount })}
          </span>
          {invalidSelectedCount > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-700">
              <AlertTriangle size={12} />
              {t("roster.importModal.invalidCount", {
                defaultValue: "{{count}} need a valid email",
                count: invalidSelectedCount,
              })}
            </span>
          )}
        </div>
        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={allChecked}
            onChange={(e) => toggleAll(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
          />
          <span className="text-gray-700 font-medium">
            {t("roster.importModal.selectAll", { defaultValue: "Select all" })}
          </span>
        </label>
      </div>

      {/* Table */}
      <div className="rounded-[10px] border border-gray-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
            <tr>
              <th className="px-2 py-2 w-10"></th>
              <th className="px-3 py-2">{t("roster.importModal.cols.firstName", { defaultValue: "First name" })}</th>
              <th className="px-3 py-2">{t("roster.importModal.cols.lastName", { defaultValue: "Last name" })}</th>
              <th className="px-3 py-2">{t("roster.importModal.cols.email", { defaultValue: "Email" })}</th>
              <th className="px-2 py-2 w-10"></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-3 py-6 text-center text-sm text-gray-500">
                  {t("roster.importModal.noRows", {
                    defaultValue: "No rows extracted. Try a different file?",
                  })}
                </td>
              </tr>
            ) : (
              rows.map((r) => {
                const emailValid = isValidEmail(r.email);
                return (
                  <tr key={r.id} className="border-t border-gray-100">
                    <td className="px-2 py-1.5">
                      <input
                        type="checkbox"
                        checked={r.selected}
                        onChange={(e) => updateRow(r.id, { selected: e.target.checked })}
                        disabled={!emailValid && !r.selected}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <EditableCell
                        value={r.firstName}
                        onChange={(v) => updateRow(r.id, { firstName: v })}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <EditableCell
                        value={r.lastName}
                        onChange={(v) => updateRow(r.id, { lastName: v })}
                      />
                    </td>
                    <td className="px-3 py-1.5">
                      <EditableCell
                        value={r.email}
                        onChange={(v) => updateRow(r.id, { email: v })}
                        placeholder="email@example.com"
                        invalid={r.email && !emailValid}
                        type="email"
                      />
                    </td>
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        onClick={() => removeRow(r.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                        title={t("roster.importModal.remove", { defaultValue: "Remove" })}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EditableCell({ value, onChange, placeholder, invalid, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={`w-full px-2 py-1 rounded-[6px] border bg-white text-sm focus:outline-none focus:border-primary ${
        invalid ? "border-red-300 bg-red-50" : "border-gray-200"
      }`}
    />
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────

function isValidEmail(email) {
  if (!email) return false;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
}

/**
 * Read a File as base64 (no data URI prefix). Uses FileReader.readAsDataURL
 * because it's the only browser-built-in path to base64; we strip the
 * "data:mime;base64," prefix so the body is just the encoded bytes.
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result || "");
      const commaIdx = result.indexOf(",");
      resolve(commaIdx >= 0 ? result.slice(commaIdx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

/**
 * Pick the most common graduationYear among extracted students. Falls
 * back to undefined if no year was inferred — caller defaults to
 * current year in that case.
 */
function mostCommonYear(students) {
  const counts = new Map();
  for (const s of students) {
    if (typeof s.graduationYear === "number") {
      counts.set(s.graduationYear, (counts.get(s.graduationYear) || 0) + 1);
    }
  }
  if (counts.size === 0) return undefined;
  let bestYear, bestCount = 0;
  for (const [year, count] of counts) {
    if (count > bestCount) {
      bestYear = year;
      bestCount = count;
    }
  }
  return bestYear;
}
